import React, { useState, useEffect, RefObject, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";

import { SearchSelect, SelectDropdown, TextInput } from "../../reusables/FormElements";
import { SVGIcon } from "../../wrappers/SVGIcon";

import { IAssessmentSaveFormProps, AssessmentField } from "../../../interfaces/assessment";
import { ISelectDropdownOption } from "../../../interfaces/formElements";
import { IEmployer, EtyLocation } from "../../../interfaces/entities";
import { TAssessmentExp, TWageType } from "../../../interfaces/entities.types";

import useGetLocations from "../../../hooks/locations/useGetLocations";
import useGetJobTitles from "../../../hooks/jobs/useGetJobTitles";
import useGetEmployers from "../../../hooks/employers/useGetEmployers";
import useGetForeignWorkersByLocation from "../../../hooks/foreign_workers/useGetForeignWorkersByLocation";
import useGetForeignWorkersByOrganization from "../../../hooks/foreign_workers/useGetForeignWorkersByOrganization";
import { useCreateAssessment, useUpdateAssessment, useDeleteResume, useDeleteJobDescription, useUploadResume, useUploadJobDescription } from "../../../hooks/assessments";

import { OPAssessmentExperiences, OPBooleanOptions, OPWageTypes } from "../../../data/options";

import { getCanadianDollarsString } from "../../../helpers/utility";

import { Icons } from "../../../data/Assets";

export const AssessmentSaveForm = (props: IAssessmentSaveFormProps) => {
    const navigate = useNavigate();

    const { operation, setAssessmentId, isLoading, assessmentData, formData, setFormData, setMainView } = props;

    const requiredFields: AssessmentField[] = ['employer', 'location', 'jobTitle', 'wageMin', 'wageMax', 'wageType', 'specialized', 'experience', 'fullTime', 'unionized', 'ipRelated', 'overseasLocation', 'otherCountry'];
    const selectFields: AssessmentField[] = ['employer', 'location', 'jobTitle', 'wageType', 'isRecordedWorker', 'worker', 'specialized', 'experience', 'fullTime', 'unionized', 'ipRelated', 'overseasLocation', 'otherCountry'];

    const resumeFileRef = useRef<HTMLInputElement>(null);
    const jobDescriptionFileRef = useRef<HTMLInputElement>(null);

    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [jobDescriptionFile, setJobDescriptionFile] = useState<File | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isResumeUploading, setIsResumeUploading] = useState(false);
    const [isJobDescriptionUploading, setIsJobDescriptionUploading] = useState(false);
    const [isResumeDeleting, setIsResumeDeleting] = useState(false);
    const [isJobDescriptionDeleting, setIsJobDescriptionDeleting] = useState(false);

    const { data: employers, isLoading: isEmployersLoading } = useGetEmployers({ sort_key: 'cra_number', sort_order: 'ASC' });
    const { data: orgLocations } = useGetLocations('organization', undefined, { sort_key: 'identifier', sort_order: 'ASC' });
    const { data: locations, isLoading: isLocationsLoading } = useGetLocations('employer', formData.employer.value?.value?.length ? formData.employer.value.value : undefined, { sort_key: 'identifier', sort_order: 'ASC' });
    const { data: workers, isLoading: isWorkersLoading } = useGetForeignWorkersByLocation(formData.location.value?.value?.length ? Number(formData.location.value.value) : 0);
    const { data: orgWorkers } = useGetForeignWorkersByOrganization();

    const { data: jobTitles, isLoading: isJobTitlesLoading } = useGetJobTitles();
    const { mutate: createAssessment } = useCreateAssessment();
    const { mutate: updateAssessment } = useUpdateAssessment();
    const { mutate: deleteResume } = useDeleteResume();
    const { mutate: deleteJobDescription } = useDeleteJobDescription();
    const { mutate: uploadResume } = useUploadResume();
    const { mutate: uploadJobDescription } = useUploadJobDescription();

    useEffect(() => {
        if (assessmentData) {
            const employer = employers?.find(employer => employer.id === assessmentData.location.employer_id);
            const location = orgLocations?.find(location => location.id === assessmentData.location_id);
            const jobTitle = jobTitles?.find(jobTitle => jobTitle.job_title === assessmentData.title);
            const wageType = OPWageTypes.find(wageType => wageType.value === assessmentData.wage_type);
            const wageMin = assessmentData.wage_min ? getCanadianDollarsString(assessmentData.wage_min.toString()) : "";
            const wageMax = assessmentData.wage_max ? getCanadianDollarsString(assessmentData.wage_max.toString()) : "";
            const specialized = OPBooleanOptions.find(option => option.value === (assessmentData.specialized ? 'true' : 'false'));
            const experience = OPAssessmentExperiences.find(exp => exp.value === assessmentData.experience);
            const fullTime = OPBooleanOptions.find(option => option.value === (assessmentData.full_time ? 'true' : 'false'));
            const unionized = OPBooleanOptions.find(option => option.value === (assessmentData.unionized ? 'true' : 'false'));
            const ipRelated = OPBooleanOptions.find(option => option.value === (assessmentData.ip_related ? 'true' : 'false'));
            const overseasLocation = OPBooleanOptions.find(option => option.value === (assessmentData.overseas_location ? 'true' : 'false'));
            const otherCountry = OPBooleanOptions.find(option => option.value === (assessmentData.other_country ? 'true' : 'false'));
            const worker = orgWorkers?.find(worker => worker.id === assessmentData.foreign_worker_id);
            const isRecordedWorker = !assessmentData.foreign_worker_id && !assessmentData.worker_name ? null : OPBooleanOptions.find(option => option.value === (assessmentData.foreign_worker_id ? 'true' : 'false'));

            setFormData({
                id: assessmentData.id,
                employer: { value: { value: employer?.id?.toString() ?? '', label: employer?.legal_name ?? '' }, error: "" },
                location: { value: { value: location?.id?.toString() ?? '', label: location?.identifier ?? '' }, error: "" },
                jobTitle: { value: { value: jobTitle?.job_title ?? '', label: jobTitle ? `${jobTitle.noc_code.noc_code} - ${jobTitle.job_title}` : '' }, error: "" },
                description: { value: assessmentData.description ?? '', error: "" },
                wageMin: { value: wageMin, error: "" },
                wageMax: { value: wageMax, error: "" },
                wageType: { value: { value: wageType?.value ?? '', label: wageType?.label ?? '' }, error: "" },
                specialized: { value: { value: specialized?.value ?? '', label: specialized?.label ?? '' }, error: "" },
                experience: { value: { value: experience?.value ?? '', label: experience?.label ?? '' }, error: "" },
                fullTime: { value: { value: fullTime?.value ?? '', label: fullTime?.label ?? '' }, error: "" },
                unionized: { value: { value: unionized?.value ?? '', label: unionized?.label ?? '' }, error: "" },
                ipRelated: { value: { value: ipRelated?.value ?? '', label: ipRelated?.label ?? '' }, error: "" },
                overseasLocation: { value: { value: overseasLocation?.value ?? '', label: overseasLocation?.label ?? '' }, error: "" },
                otherCountry: { value: { value: otherCountry?.value ?? '', label: otherCountry?.label ?? '' }, error: "" },
                isRecordedWorker: { value: { value: isRecordedWorker?.value ?? 'false', label: isRecordedWorker?.label ?? 'No' }, error: "" },
                worker: { value: { value: worker?.id.toString() ?? '', label: worker?.worker_details[0] ? `${worker?.worker_details[0].first_name} ${worker?.worker_details[0].last_name}` : '' }, error: "" },
                workerName: { value: assessmentData.worker_name ?? '', error: "" },
            });
        };

        // eslint-disable-next-line
    }, [assessmentData, employers, orgLocations, orgWorkers, jobTitles])

    const onChangeText = (name: AssessmentField, value: string, from?: string) => {
        setFormData({
            ...formData,
            [name]: { value, error: "" },
        });
    };

    const onChangeSelect = (name: AssessmentField, option: ISelectDropdownOption) => {
        setFormData(prevData => {
            const newData = {
                ...prevData,
                [name]: { value: option, error: "" },
            };

            if (name === "employer") {
                newData.location = { value: { value: '', label: '' }, error: "" };
            };

            if (name === "location" || name === "employer") {
                newData.worker = { value: { value: '', label: '' }, error: "" };
                newData.workerName = { value: '', error: "" };
            };

            return newData;
        });
    };

    const getProcessing = () => {
        if (isResumeUploading || isJobDescriptionUploading || isResumeDeleting || isJobDescriptionDeleting || isProcessing || isLoading) return true;
        return false;
    };

    const isSaveDisabled = () => {
        const emptyFields = requiredFields.some(field => {
            let value = "";
            if (selectFields.includes(field)) {
                const dataValue = formData[field].value as ISelectDropdownOption;
                value = dataValue.value;
            } else {
                value = formData[field].value as string;
            };
            return !value?.trim()?.length;
        });

        const isDataSame =
            formData.employer.value.value === (assessmentData?.location?.employer_id?.toString() ?? '') &&
            formData.location.value.value === (assessmentData?.location_id?.toString() ?? '') &&
            formData.jobTitle.value.value === (assessmentData?.title ?? '') &&
            formData.description.value === (assessmentData?.description ?? '') &&
            formData.wageMin.value.replace(/,/g, '') === (assessmentData?.wage_min?.toString() ?? '') &&
            formData.wageMax.value.replace(/,/g, '') === (assessmentData?.wage_max?.toString() ?? '') &&
            formData.wageType.value.value === (assessmentData?.wage_type ?? '') &&
            formData.specialized.value.value === (assessmentData?.specialized ? 'true' : 'false') &&
            formData.experience.value.value === (assessmentData?.experience ?? '') &&
            formData.fullTime.value.value === (assessmentData?.full_time ? 'true' : 'false') &&
            formData.unionized.value.value === (assessmentData?.unionized ? 'true' : 'false') &&
            formData.ipRelated.value.value === (assessmentData?.ip_related ? 'true' : 'false') &&
            formData.overseasLocation.value.value === (assessmentData?.overseas_location ? 'true' : 'false') &&
            formData.otherCountry.value.value === (assessmentData?.other_country ? 'true' : 'false') &&
            // formData.isRecordedWorker.value.value === (assessmentData?.foreign_worker_id ? 'true' : 'false') &&
            formData.worker.value?.value === (assessmentData?.foreign_worker_id?.toString() ?? '') &&
            formData.workerName.value === (assessmentData?.worker_name ?? '');

        return emptyFields || isDataSame;
    };

    const onOpenFile = (fileRef: RefObject<HTMLInputElement>) => {
        if (getProcessing()) return;

        if (fileRef.current) {
            fileRef.current.click();
        };
    };

    const onFileChange = (fileRef: RefObject<HTMLInputElement>, fileType: 'resume' | 'jobDescription') => {
        if (getProcessing()) return;

        if (fileRef.current?.files) {
            const file = fileRef.current.files[0];

            if (fileType === 'resume') {
                setResumeFile(file);
            } else if (fileType === 'jobDescription') {
                setJobDescriptionFile(file);
            }

            fileRef.current.value = '';

            if (formData.id) {
                if (fileType === 'resume') {
                    uploadResume({
                        id: formData.id,
                        file,
                        setIsResumeUploading,
                        errorCallback: () => setResumeFile(null),
                    });
                } else if (fileType === 'jobDescription') {
                    uploadJobDescription({
                        id: formData.id,
                        file,
                        setIsJobDescriptionUploading,
                        errorCallback: () => setJobDescriptionFile(null),
                    });
                }
            };
        };
    };

    const onSaveData = () => {
        if (isSaveDisabled() || getProcessing()) return;

        // clear all errors
        setFormData(prevData => {
            const newData = { ...prevData };
            Object.keys(newData).forEach(key => {
                if (key === "id") return;
                newData[key as AssessmentField].error = "";
            });
            return newData;
        });

        setIsProcessing(true);

        const jobTitle = jobTitles?.find(jobTitle => jobTitle.job_title === formData.jobTitle.value.value);
        if (!jobTitle) {
            setIsProcessing(false);
            return;
        };

        const payload = {
            location_id: Number(formData.location.value.value),
            title: formData.jobTitle.value.value,
            noc: jobTitle.noc_code.noc_code,
            description: formData.description.value,
            wage_min: Number(formData.wageMin.value.replace(/,/g, '')),
            wage_max: Number(formData.wageMax.value.replace(/,/g, '')),
            wage_type: formData.wageType.value.value as TWageType,
            specialized: formData.specialized.value.value === 'true',
            experience: formData.experience.value.value as TAssessmentExp,
            full_time: formData.fullTime.value.value === 'true',
            unionized: formData.unionized.value.value === 'true',
            ip_related: formData.ipRelated.value.value === 'true',
            overseas_location: formData.overseasLocation.value.value === 'true',
            other_country: formData.otherCountry.value.value === 'true',
            foreign_worker_id: formData.worker.value?.value?.length && formData.isRecordedWorker.value.value === 'true' ? Number(formData.worker.value.value) : null,
            worker_name: formData.workerName.value?.length && !formData.worker.value?.value?.length && formData.isRecordedWorker.value.value === 'false' ? formData.workerName.value : null,
        };

        if (!formData.id) {
            createAssessment({
                payload: { ...payload, resumeFile, jobDescriptionFile },
                setIsProcessing,
                setMainView,
                setAssessmentId,
            });
        } else {
            updateAssessment({
                payload: { id: formData.id, ...payload },
                setIsProcessing,
                setMainView,
            });
        };
    };

    const onDeleteResume = () => {
        if (getProcessing() || !formData.id) return;

        if (!formData.id) {
            setResumeFile(null);
        } else {
            deleteResume({
                id: formData.id,
                setIsResumeDeleting,
            });
        };
    };

    const onDeleteJobDescription = () => {
        if (getProcessing()) return;

        if (!formData.id) {
            setJobDescriptionFile(null);
        } else {
            deleteJobDescription({
                id: formData.id,
                setIsJobDescriptionDeleting,
            });
        };
    };

    return (
        <div className='immployer__details_form_view'>
            <div className="immployer__assessment__upload_actions">
                <input className='immployer__file_upload_sudo_input' ref={resumeFileRef} type='file' accept='application/pdf' onChange={() => onFileChange(resumeFileRef, 'resume')} />
                {assessmentData?.resume_url
                    ? <div className="immployer__file_upload_preview">
                        <a href={assessmentData.resume_url} target="_blank" rel="noreferrer">
                            View Resume
                        </a>
                        <button onClick={onDeleteResume}>
                            {isResumeDeleting
                                ? <i className='immployer__btn_loader' />
                                : <SVGIcon src={Icons.Trash3Fill} />
                            }
                        </button>
                    </div>
                    : <Fragment>
                        {resumeFile
                            ? <div className="immployer__file_upload_preview">
                                <span>{resumeFile.name}</span>
                                <button onClick={onDeleteResume}>
                                    <SVGIcon src={Icons.Trash3Fill} />
                                </button>
                            </div>
                            : <button className="immployer__btn immployer__btn__tertiary" onClick={() => onOpenFile(resumeFileRef)}>
                                {isResumeUploading
                                    ? <i className='immployer__btn_loader' />
                                    : <SVGIcon src={Icons.Upload2} />
                                }
                                <span>Resume</span>
                            </button>
                        }
                    </Fragment>
                }
                <input className='immployer__file_upload_sudo_input' ref={jobDescriptionFileRef} type='file' accept='application/pdf' onChange={() => onFileChange(jobDescriptionFileRef, 'jobDescription')} />
                {assessmentData?.job_description_url
                    ? <div className="immployer__file_upload_preview">
                        <a href={assessmentData.job_description_url} target="_blank" rel="noreferrer">
                            View Job Description
                        </a>
                        <button onClick={onDeleteJobDescription}>
                            {isJobDescriptionDeleting
                                ? <i className='immployer__btn_loader' />
                                : <SVGIcon src={Icons.Trash3Fill} />
                            }
                        </button>
                    </div>
                    : <Fragment>
                        {jobDescriptionFile
                            ? <div className="immployer__file_upload_preview">
                                <span>{jobDescriptionFile.name}</span>
                                <button onClick={onDeleteJobDescription}>
                                    <SVGIcon src={Icons.Trash3Fill} />
                                </button>
                            </div>
                            : <button className="immployer__btn immployer__btn__tertiary" onClick={() => onOpenFile(jobDescriptionFileRef)}>
                                {isJobDescriptionUploading
                                    ? <i className='immployer__btn_loader' />
                                    : <SVGIcon src={Icons.Upload2} />
                                }
                                <span>Job Description</span>
                            </button>
                        }
                    </Fragment>
                }
            </div>
            <div className="immployer__details_form_inputs__container">
                <div className="immployer__details_form_inputs">
                    <SelectDropdown
                        name="employer"
                        label="Select Employer"
                        required={true}
                        placeholder="-- select employer --"
                        value={formData.employer.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("employer", option)}
                        errorMsg={formData.employer.error}
                        options={!employers?.length ? [] : employers?.map((employer: IEmployer) => ({ value: employer.id.toString(), label: employer.legal_name }))}
                        read_only={isLoading || isEmployersLoading}
                        addNewBtnData={{
                            name: "Employer",
                            label: "Add Employer",
                            action: () => navigate('/employers/new'),
                        }}
                    />
                    <SelectDropdown
                        name="location"
                        label="Select Location"
                        required={true}
                        placeholder="-- select location --"
                        value={formData.location.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("location", option)}
                        errorMsg={formData.location.error}
                        options={!locations?.length ? [] : locations?.map((location: EtyLocation) => ({ value: location.id.toString(), label: location.identifier }))}
                        read_only={isLoading || isLocationsLoading || !formData.employer.value?.value?.length}
                        addNewBtnData={{
                            name: "Location",
                            label: "Add Location",
                            action: () => navigate(`/employers/${formData.employer.value.value}/locations/new`),
                        }}
                    />
                    <SelectDropdown
                        name="isRecordedWorker"
                        label="Is the worker already entered into Immployer?"
                        placeholder="-- select --"
                        value={formData.isRecordedWorker.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("isRecordedWorker", option)}
                        errorMsg={formData.isRecordedWorker.error}
                        options={OPBooleanOptions}
                        read_only={isLoading}
                        length_flex={2}
                    />
                    {formData.isRecordedWorker.value.value === 'true'
                        ? <SearchSelect
                            name="worker"
                            label="Foreign Worker (if known)"
                            placeholder="Enter worker name"
                            value={formData.worker.value}
                            onChange={(option: ISelectDropdownOption) => onChangeSelect("worker", option)}
                            errorMsg={formData.worker.error}
                            options={!workers?.length ? [] : workers.map(worker => ({ value: worker.id.toString(), label: `${worker.worker_details[0].first_name} ${worker.worker_details[0].last_name}` }))}
                            read_only={isLoading || isWorkersLoading || !workers?.length || !formData.location.value?.value?.length}
                        />
                        : <TextInput
                            name="workerName"
                            type="text"
                            max_length={100}
                            label="Foreign Worker (if known)"
                            errorMsg={formData.workerName.error}
                            placeholder="Enter worker name"
                            onChange={(value: string) => onChangeText("workerName", value)}
                            value={formData.workerName.value}
                            read_only={isLoading || !formData.location.value?.value?.length || formData.isRecordedWorker.value.value !== 'false'}
                        />
                    }
                    <SearchSelect
                        name="jobTitle"
                        label="Job Title"
                        required={true}
                        placeholder="Search job title"
                        value={formData.jobTitle.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("jobTitle", option)}
                        errorMsg={formData.jobTitle.error}
                        options={!jobTitles?.length ? [] : jobTitles.map(jobTitle => ({ value: jobTitle.job_title, label: `${jobTitle.noc_code.noc_code} - ${jobTitle.job_title}` }))}
                        read_only={isLoading || isJobTitlesLoading || !jobTitles?.length}
                        length_flex={2}
                    />
                    <SelectDropdown
                        name="experience"
                        label="Experience required"
                        required={true}
                        placeholder="-- select experience --"
                        value={formData.experience.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("experience", option)}
                        errorMsg={formData.experience.error}
                        options={OPAssessmentExperiences}
                        read_only={isLoading}
                        length_flex={2}
                    />
                    <SelectDropdown
                        name="fullTime"
                        label="Is the role full time? (30+ hours/week)"
                        required={true}
                        placeholder="-- select --"
                        value={formData.fullTime.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("fullTime", option)}
                        errorMsg={formData.fullTime.error}
                        options={OPBooleanOptions}
                        read_only={isLoading}
                        length_flex={2}
                    />
                    <TextInput
                        name="wageMin"
                        type="text"
                        pattern_mask="currency_cad"
                        label="Minimum Wage"
                        required={true}
                        errorMsg={formData.wageMin.error}
                        placeholder="Minimum wage"
                        onChange={(value: string) => onChangeText("wageMin", value)}
                        value={formData.wageMin.value}
                        read_only={isLoading}
                    />
                    <TextInput
                        name="wageMax"
                        type="text"
                        pattern_mask="currency_cad"
                        label="Maximum Wage"
                        required={true}
                        errorMsg={formData.wageMax.error}
                        placeholder="Maximum wage"
                        onChange={(value: string) => onChangeText("wageMax", value)}
                        value={formData.wageMax.value}
                        read_only={isLoading}
                    />
                    <SelectDropdown
                        name="wageType"
                        label="Wage Type"
                        required={true}
                        placeholder="-- select wage type --"
                        value={formData.wageType.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("wageType", option)}
                        errorMsg={formData.wageType.error}
                        options={OPWageTypes}
                        read_only={isLoading}
                    />
                    <SelectDropdown
                        name="unionized"
                        label="Is this position unionized?"
                        required={true}
                        placeholder="-- select --"
                        value={formData.unionized.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("unionized", option)}
                        errorMsg={formData.unionized.error}
                        options={OPBooleanOptions}
                        read_only={isLoading}
                        length_flex={2}
                    />
                    <SelectDropdown
                        name="specialized"
                        label="Is this position highly specialized?"
                        required={true}
                        placeholder="-- select --"
                        value={formData.specialized.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("specialized", option)}
                        errorMsg={formData.specialized.error}
                        options={OPBooleanOptions}
                        read_only={isLoading}
                        length_flex={2}
                    />
                    <SelectDropdown
                        name="ipRelated"
                        label="Does this role involve contributing to the creation of intellectual property (IP)?"
                        required={true}
                        placeholder="-- select --"
                        value={formData.ipRelated.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("ipRelated", option)}
                        errorMsg={formData.ipRelated.error}
                        options={OPBooleanOptions}
                        read_only={isLoading}
                        length_flex={2}
                    />
                    <SelectDropdown
                        name="overseasLocation"
                        label="Do you have any overseas locations or branches?"
                        required={true}
                        placeholder="-- select --"
                        value={formData.overseasLocation.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("overseasLocation", option)}
                        errorMsg={formData.overseasLocation.error}
                        options={OPBooleanOptions}
                        read_only={isLoading}
                        length_flex={2}
                    />
                    <SelectDropdown
                        name="otherCountry"
                        label="Do your Canadian employees work in other countries?"
                        required={true}
                        placeholder="-- select --"
                        value={formData.otherCountry.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("otherCountry", option)}
                        errorMsg={formData.otherCountry.error}
                        options={OPBooleanOptions}
                        read_only={isLoading}
                        length_flex={2}
                    />
                </div>
            </div>
            <div className="immployer__details_form_actions">
                <button
                    className={`immployer__btn immployer__btn__secondary ${getProcessing() ? "immployer__btn__secondary__disabled" : ""}`}
                    onClick={() => getProcessing() ? null : navigate('/assessments')}
                >
                    {operation === 'create' ? "Discard" : "Go back"}
                </button>
                <button
                    className={`immployer__btn immployer__btn__primary ${isSaveDisabled() ? "immployer__btn__primary__disabled" : ""}`}
                    onClick={onSaveData}
                >
                    {isProcessing ? <i className='immployer__btn_loader' /> : 'Save'}
                </button>
            </div>
        </div>
    );
};